import type { LegalClassRestriction } from "@models/legalClassRestriction";
import { legalClassLabel } from "@models/product";

export type RestrictionLifecycle = "scheduled" | "active" | "expired";

// A zone designator at the end of an ISO datetime: "Z", "+05:00" or "+0500".
const ZONE_SUFFIX = /(?:Z|[+-]\d{2}:?\d{2})$/;

/**
 * The wire value as an instant, always read as UTC.
 *
 * The API's dates ARE UTC: `UsnaraDbContext` applies `UtcDateTimeConverter` to
 * every DateTime, and `LegalClassRestrictionService.EvaluateAsync` compares the
 * stored value to `DateTime.UtcNow`. `new Date(value)` on its own would leave
 * that to the string's format — an ISO datetime with no zone designator (the
 * exact shape this screen POSTs) parses as *local* time, which would move the
 * badge's boundary by the browser's offset and make the screen disagree with
 * the server about what is in force. Pinning the parse to UTC is what makes
 * "Active" here mean "blocking checkout right now" there.
 */
export const toRuleInstant = (value: string): number =>
    Date.parse(ZONE_SUFFIX.test(value) ? value : `${value}Z`);

/**
 * Where the API's effective window puts this rule relative to `now`.
 *
 * The window is half-open — `[startDate, endDate)` — matching
 * `LegalClassRestrictionService.EvaluateAsync`, which filters on
 * `StartDate <= asOf && (EndDate == null || EndDate > asOf)`. A rule whose
 * endDate has passed is expired, not deleted; the list shows that state rather
 * than hiding the row, so an admin can see why a rule stopped applying.
 */
export const getRestrictionLifecycle = (
    restriction: Pick<LegalClassRestriction, "startDate" | "endDate">,
    now: Date,
): RestrictionLifecycle => {
    const nowMs = now.getTime();

    if (
        restriction.endDate !== null &&
        toRuleInstant(restriction.endDate) <= nowMs
    ) {
        return "expired";
    }

    if (toRuleInstant(restriction.startDate) > nowMs) {
        return "scheduled";
    }

    return "active";
};

/** "CA" or "ZIP 90210" — exactly one of the two is set on every rule. */
export const describeDestination = (
    restriction: Pick<LegalClassRestriction, "state" | "zipCode">,
): string => (restriction.state ? restriction.state : `ZIP ${restriction.zipCode}`);

/**
 * What deleting this rule changes for customers *today*, in plain language.
 *
 * The `isLegal` branch is load-bearing, not decoration: the evaluation query
 * filters on `!r.IsLegal`, so an explicit-allow row is never read and can never
 * override anything. Telling an admin that deleting one "un-restricts
 * products" would be false.
 */
export const describeDeleteImpact = (
    restriction: LegalClassRestriction,
    now: Date,
): string => {
    const className = legalClassLabel(restriction.legalClass);
    const destination = describeDestination(restriction);
    const lifecycle = getRestrictionLifecycle(restriction, now);

    if (lifecycle === "expired") {
        // Not "expired ON <end date>": the end date is the first day the rule
        // is no longer in force, so that phrasing reads as the last day the
        // rule covered and overstates the ban by a day.
        return `This rule stopped applying at the start of ${formatRestrictionDate(restriction.endDate)} — the end date is excluded from the window — so it affects nothing today. Deleting it removes the record that it was ever in force.`;
    }

    if (lifecycle === "scheduled") {
        return `This rule has not taken effect yet — it starts on ${formatRestrictionDate(restriction.startDate)}. Deleting it means it never will.`;
    }

    if (restriction.isLegal) {
        return `This rule records ${className} as legal in ${destination}. Explicit-allow rules never block anything on their own, so deleting it does not change what customers can buy.`;
    }

    return `This rule is in force today: ${className} products are blocked in ${destination} across browse, search, product pages and checkout. Deleting it makes them available in ${destination} immediately.`;
};

/**
 * ISO datetime → the `yyyy-MM-dd` an `<input type="date">` needs. Slicing
 * rather than going through `Date` deliberately: parsing "2026-01-01T00:00:00Z"
 * and re-formatting it in a negative-offset timezone yields 2025-12-31, which
 * would silently shift a compliance date by a day every time a rule is edited.
 */
export const toDateInputValue = (value: string | null): string =>
    value ? value.slice(0, 10) : "";

// yyyy-MM-dd, as an ISO datetime carries it.
const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The rule's date, for display. Built from the same slice the edit dialog
 * seeds, so one rule shows one date everywhere on this screen.
 *
 * `@utils/formatter`'s `formatDate` cannot be used here: it round-trips through
 * `new Date(...).toLocaleDateString()`, so a rule starting at midnight UTC —
 * every rule this screen writes — renders as the PREVIOUS day everywhere west
 * of Greenwich, while the edit dialog shows the day the admin typed. Nothing
 * below constructs a `Date`, so there is no offset for a timezone to apply.
 * Placeholder and format otherwise match `formatDate`.
 */
export const formatRestrictionDate = (value: string | null): string => {
    const calendarDate = toDateInputValue(value);

    if (!CALENDAR_DATE.test(calendarDate)) {
        return "—";
    }

    const [year, month, day] = calendarDate.split("-");
    return `${Number(month)}/${Number(day)}/${year}`;
};
